/**
 * Label Layout Editor Store
 *
 * Zustand store with zundo temporal middleware for undo/redo support.
 * Manages template data and editor UI state.
 *
 * Architecture notes:
 * - This is the FIRST use of Zustand in this codebase
 * - Zustand handles LOCAL UI state (editor positions, drag state)
 * - TanStack Query continues to handle SERVER state (API data)
 * - They complement each other, don't overlap
 */

import { create } from 'zustand';
import { temporal, type TemporalState } from 'zundo';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import type {
  CanvasSize,
  ComponentConfig,
  ComponentType,
  EditorState,
  LabelType,
  SampleScenarioKey,
} from './label-layout-types';
import { CANVAS_PRESETS, COMPONENT_CONSTRAINTS, SAMPLE_SCENARIOS } from './label-layout-types';

/** All known component types from the schema */
const ALL_COMPONENT_TYPES = Object.keys(COMPONENT_CONSTRAINTS) as ComponentType[];

/**
 * Ensure a template's components array includes all known component types.
 * Older saved templates may be missing newer types (e.g., orderNumberBarcode).
 * Missing types are added as hidden with a default position/size.
 */
function ensureAllComponentTypes(components: ComponentConfig[]): ComponentConfig[] {
  const existingTypes = new Set(components.map(c => c.type));
  const missing = ALL_COMPONENT_TYPES.filter(t => !existingTypes.has(t));
  if (missing.length === 0) return components;

  const additions: ComponentConfig[] = missing.map(type => {
    const constraints = COMPONENT_CONSTRAINTS[type];
    return {
      id: type,
      type,
      position: { x: 0, y: 0 },
      size: { width: constraints.minWidth ?? 20, height: constraints.minHeight ?? 8 },
      visible: false,
      locked: false,
      zIndex: 1,
    };
  });

  return [...components, ...additions];
}

// ============================================================================
// STATE INTERFACE
// ============================================================================

export interface LabelLayoutState {
  // Template data (persisted to server, tracked in undo history)
  templateId: string | null;
  templateName: string;
  canvasSize: CanvasSize;
  components: ComponentConfig[];
  labelType: LabelType;
  isDefault: boolean;
  isBuiltIn: boolean;

  // Editor state (UI-only, NOT tracked in undo history)
  editor: EditorState;
  isDirty: boolean;

  // Actions
  setTemplate: (template: {
    id: string;
    name: string;
    canvasSize: CanvasSize;
    components: ComponentConfig[];
    labelType: LabelType;
    isDefault: boolean;
    isBuiltIn: boolean;
  }) => void;
  updateComponent: (id: string, update: Partial<ComponentConfig>) => void;
  setComponents: (components: ComponentConfig[]) => void;
  toggleVisibility: (id: string) => void;
  setCanvasSize: (size: CanvasSize) => void;
  setLabelType: (type: LabelType) => void;
  setSelectedComponent: (id: string | null) => void;
  setScale: (scale: number) => void;
  toggleGrid: () => void;
  toggleSnap: () => void;
  setPreviewScenario: (scenario: SampleScenarioKey) => void;
  markClean: () => void;
  reset: () => void;
}

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialEditorState: EditorState = {
  selectedComponentId: null,
  // Responsive default: 2x on mobile, 3x on desktop
  scale: typeof window !== 'undefined' && window.innerWidth < 768 ? 2 : 3,
  showGrid: true,
  snapToGrid: true,
  gridSizeMm: 5,
  previewScenario: 'standard',
};

const initialCanvasSize = CANVAS_PRESETS['compact-32x57'];

// ============================================================================
// STORE WITH TEMPORAL (UNDO/REDO) MIDDLEWARE
// ============================================================================

export const useLabelLayoutStore = create<LabelLayoutState>()(
  temporal(
    (set) => ({
      // Initial state
      templateId: null,
      templateName: 'Untitled Template',
      canvasSize: initialCanvasSize,
      components: [],
      labelType: 'BOTH',
      isDefault: false,
      isBuiltIn: false,
      editor: initialEditorState,
      isDirty: false,

      // Actions
      setTemplate: (template) =>
        set({
          templateId: template.id,
          templateName: template.name,
          canvasSize: template.canvasSize,
          components: ensureAllComponentTypes(template.components),
          labelType: template.labelType,
          isDefault: template.isDefault,
          isBuiltIn: template.isBuiltIn,
          isDirty: false,
        }),

      updateComponent: (id, update) =>
        set((state) => ({
          components: state.components.map((c) =>
            c.id === id ? { ...c, ...update } : c
          ),
          isDirty: true,
        })),

      setComponents: (components) => set({ components, isDirty: true }),

      toggleVisibility: (id) =>
        set((state) => ({
          components: state.components.map((c) =>
            c.id === id ? { ...c, visible: !c.visible } : c
          ),
          isDirty: true,
        })),

      setCanvasSize: (size) => set({ canvasSize: size, isDirty: true }),

      setLabelType: (type) => set({ labelType: type, isDirty: true }),

      setSelectedComponent: (id) =>
        set((state) => ({
          editor: { ...state.editor, selectedComponentId: id },
        })),

      setScale: (scale) =>
        set((state) => ({
          editor: { ...state.editor, scale: Math.max(1, Math.min(5, scale)) },
        })),

      toggleGrid: () =>
        set((state) => ({
          editor: { ...state.editor, showGrid: !state.editor.showGrid },
        })),

      toggleSnap: () =>
        set((state) => ({
          editor: { ...state.editor, snapToGrid: !state.editor.snapToGrid },
        })),

      setPreviewScenario: (scenario) =>
        set((state) => ({
          editor: { ...state.editor, previewScenario: scenario },
        })),

      markClean: () => set({ isDirty: false }),

      reset: () =>
        set({
          templateId: null,
          templateName: 'Untitled Template',
          canvasSize: initialCanvasSize,
          components: [],
          labelType: 'BOTH',
          isDefault: false,
          isBuiltIn: false,
          editor: initialEditorState,
          isDirty: false,
        }),
    }),
    {
      // Undo/redo configuration
      limit: 50, // Prevent memory bloat
      partialize: (state) => ({
        // Only track layout changes in undo history, NOT editor UI state
        components: state.components,
        canvasSize: state.canvasSize,
        templateName: state.templateName,
        labelType: state.labelType,
        isDefault: state.isDefault,
      }),
    }
  )
);

// ============================================================================
// TEMPORAL STORE HOOK
// ============================================================================

/**
 * Hook for accessing undo/redo state.
 * Use to build undo/redo UI buttons.
 *
 * @example
 * const { undo, redo, pastStates, futureStates } = useTemporalStore((state) => state);
 * const canUndo = pastStates.length > 0;
 */
type PartializedState = Pick<
  LabelLayoutState,
  'components' | 'canvasSize' | 'templateName' | 'labelType' | 'isDefault'
>;

export const useTemporalStore = <T,>(
  selector: (state: TemporalState<PartializedState>) => T
) => useStoreWithEqualityFn(useLabelLayoutStore.temporal, selector);

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

/**
 * Returns the current sample data based on selected scenario.
 */
export function useCurrentSampleData() {
  const scenario = useLabelLayoutStore((s) => s.editor.previewScenario);
  return SAMPLE_SCENARIOS[scenario];
}

/**
 * Returns the currently selected component, or null.
 */
export function useSelectedComponent() {
  const selectedId = useLabelLayoutStore((s) => s.editor.selectedComponentId);
  const components = useLabelLayoutStore((s) => s.components);
  return selectedId ? components.find((c) => c.id === selectedId) : null;
}

/**
 * Returns true if the layout has unsaved changes.
 */
export function useIsDirty() {
  return useLabelLayoutStore((s) => s.isDirty);
}

/**
 * Returns the current scale factor.
 */
export function useScale() {
  return useLabelLayoutStore((s) => s.editor.scale);
}

/**
 * Returns grid settings.
 */
export function useGridSettings() {
  return useLabelLayoutStore((s) => ({
    showGrid: s.editor.showGrid,
    snapToGrid: s.editor.snapToGrid,
    gridSizeMm: s.editor.gridSizeMm,
  }));
}
